import { useCallback, useEffect, useRef, useState } from "react";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { EditDatePicker } from "@/components/ui/EditDatePicker";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useGoogleTranslateRefresh } from "@/hooks/useGoogleTranslateRefresh";
import type { PlaceResult } from "@/hooks/useGooglePlacesAutocomplete";
import {
  extractAptUnit,
  parseAddressComponents,
} from "@/lib/utils/addressParser";
import { formatAddressDisplay } from "@/features/applicant/utils/addressHelpers";
import {
  geocodeAddressRaw,
  matchCityStateZipResidentGeocodeStyle,
} from "@/lib/utils/geocoding";
import { CircleAlert } from "lucide-react";
import styles from "../../../styles/applicationDetail.module.css";
import editStyles from "../../../styles/editForm.module.css";
import * as angErr from "../../../utils/aboutYourselfAngularInlineMessages";
import { normalizeDateFromAPI } from "../../../utils/applicationFormatters";
import {
  applyMoveInValidateMonths,
  computePreviousRowMonths,
} from "../../../utils/previousAddressLogic";
import { RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL } from "@/features/invitee/utils/inviteeValidators";
import {
  ADDRESS_TYPES,
  RELATIONSHIP_TYPES,
  US_STATE_OPTIONS,
  aboutAddressTestId,
  formatPhoneInput,
  isUsCountry,
} from "../aboutYourselfTabHelpers";
import { inputErrorClass, withInputError } from "./aboutYourselfEditFieldHelpers";
import type {
  AboutYourselfAddressEditFormData,
  AddressEditFormProps,
} from "../../../types/tabs";
import type { AddressLike, PreviousAddressRow } from "../../../types/utils";

const MILITARY_STATES = ["AA", "AE", "AP"] as const;
const GEOCODE_WARNING_MESSAGE =
  "We were unable to verify this address with Google Maps. If you know it is correct, you may continue";

function AngularShowControlError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className={editStyles.showControlError} role="alert">
      <CircleAlert className={editStyles.showControlErrorIcon} aria-hidden />
      <span>{message}</span>
    </div>
  );
}

export function AddressEditForm({
  addressData,
  addressType,
  addressIndex,
  previousAddressEndMoveInDate,
  onAddressChange,
  exposeInlineValidation = false,
}: AddressEditFormProps) {
  const formatAddressSearchValue = (
    addr: AddressLike | PreviousAddressRow | null | undefined,
  ): string => {
    const hasAddressParts = [
      addr?.house_no,
      addr?.street,
      addr?.street_type,
      addr?.apartment,
      addr?.city,
      addr?.state,
      addr?.zip_code,
    ].some((value) => String(value ?? "").trim().length > 0);
    return hasAddressParts ? formatAddressDisplay(addr ?? {}) : "";
  };

  // Parse date for HTML input (YYYY-MM-DD); ISO API dates like Angular.
  const parseDate = (dateValue: unknown): string => {
    if (!dateValue) return "";
    if (typeof dateValue === "string") {
      const normalized = normalizeDateFromAPI(dateValue);
      if (normalized) return normalized;
      // DD/MM/YYYY → YYYY-MM-DD for form input
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateValue)) {
        const [day, month, year] = dateValue.split("/");
        return `${year}-${month}-${day}`;
      }
    }
    if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, "0");
      const day = String(dateValue.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  const getInitialFormData = (): AboutYourselfAddressEditFormData => {
    if (!addressData) {
      return {
        ra_id: "",
        street_address: "",
        outside_us: false,
        house_no: "",
        street: "",
        apartment: "",
        city: "",
        state: "",
        zip_code: "",
        country: "US",
        address_type: "RENT",
        paid_rent: "",
        rent_amount: "",
        month_reside: "",
        moveIn_date: "",
        moveOut_date: "",
        currently_living: false,
        landlord_name: "",
        landlord_email: "",
        landlord_mobile_no: "",
        landlord_relationship_type: "",
      };
    }
    return {
      // ra_id, street_address, outside_us + address fields
      ra_id: addressData?.ra_id || "",
      street_address: formatAddressSearchValue(addressData),
      outside_us:
        Boolean(addressData?.outside_us) ||
        !isUsCountry(addressData?.country || "US"),
      // Basic address fields
      house_no: addressData?.house_no || "",
      street: addressData?.street || "",
      apartment: addressData?.apartment || "",
      city: addressData?.city || "",
      state: addressData?.state || "",
      zip_code: addressData?.zip_code || "",
      country: addressData?.country || "US",
      address_type: addressData?.address_type || "RENT",
      // Address data fields
      paid_rent: addressData?.address_data?.paid_rent || "",
      rent_amount: addressData?.address_data?.rent_amount || "",
      month_reside: addressData?.address_data?.month_reside || "",
      moveIn_date: parseDate(addressData?.address_data?.moveIn_date),
      moveOut_date: parseDate(addressData?.address_data?.moveOut_date),
      currently_living:
        addressData?.address_data?.currently_living === "1" ||
        addressData?.address_data?.currently_living === true,
      landlord_name: addressData?.address_data?.landlord_name || "",
      landlord_email: addressData?.address_data?.landlord_email || "",
      landlord_mobile_no: addressData?.address_data?.landlord_mobile_no || "",
      landlord_relationship_type:
        addressData?.address_data?.landlord_relationship_type || "",
    };
  };

  const [formData, setFormData] = useState<AboutYourselfAddressEditFormData>(getInitialFormData);
  // Previous addresses open by default — no Angular "View Detail" toggle.
  const [showDetail, setShowDetail] = useState(true);
  const [addressFieldsDirty, setAddressFieldsDirty] = useState<
    Record<string, boolean>
  >({});
  const [geocodeWarning, setGeocodeWarning] = useState<string | null>(null);
  const processGeocodeRef = useRef(false);
  const resetIdentity = `${addressType}:${addressIndex ?? 0}:${addressData?.ra_id || "__new__"}`;
  const lastResetIdentityRef = useRef(resetIdentity);

  // Angular keeps these fields in the DOM (d-none) so Google Translate scans them once.
  // React mounts them on demand, so re-run translateHtml when this row, its detail, or the
  // Own/Rent + currently-living branches (which reveal new labeled fields) appear.
  useGoogleTranslateRefresh(
    showDetail,
    formData.address_type,
    formData.currently_living,
  );

  const toComparableYmd = (value: string | undefined | null): string => {
    return parseDate(value || "");
  };

  // Re-init only when address row identity changes — not every keystroke object.
  useEffect(() => {
    if (!addressData || resetIdentity === lastResetIdentityRef.current) return;
    lastResetIdentityRef.current = resetIdentity;
    setFormData(getInitialFormData());
    setAddressFieldsDirty({});
    setGeocodeWarning(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetIdentity]);

  /** Angular `geocodeAddress()` — current address only; soft warning on city/state/zip blur. */
  const handleGeocodeCheck = useCallback(async () => {
    if (addressType !== "current_address") return;

    const city = String(formData.city ?? "").trim();
    const state = String(formData.state ?? "").trim();
    const zipCode = String(formData.zip_code ?? "").trim();

    if (MILITARY_STATES.includes(state.toUpperCase() as (typeof MILITARY_STATES)[number])) {
      setGeocodeWarning(null);
      return;
    }

    if (!city || !state || !zipCode || processGeocodeRef.current) return;

    processGeocodeRef.current = true;
    try {
      const { result, status } = await geocodeAddressRaw(`${city}, ${state}, ${zipCode}`);
      if (
        status !== "OK" ||
        !result ||
        !matchCityStateZipResidentGeocodeStyle(result, city, state, zipCode)
      ) {
        setGeocodeWarning(GEOCODE_WARNING_MESSAGE);
      } else {
        setGeocodeWarning(null);
      }
    } catch {
      setGeocodeWarning(GEOCODE_WARNING_MESSAGE);
    } finally {
      processGeocodeRef.current = false;
    }
  }, [addressType, formData.city, formData.state, formData.zip_code]);

  // Angular `moveInValidate` — parent runs full pipeline on `onAddressChange`.
  useEffect(() => {
    if (addressType !== "current_address") return;
    const merged: Record<string, unknown> = {
      ...formData,
      address_type: formData.address_type,
      address_data: {
        ...(typeof addressData?.address_data === "object" && addressData.address_data
          ? addressData.address_data
          : {}),
        moveIn_date: formData.moveIn_date,
        moveOut_date: formData.moveOut_date,
        currently_living: formData.currently_living,
        paid_rent: formData.paid_rent,
        month_reside: formData.month_reside,
      },
    };
    const next = applyMoveInValidateMonths(merged as PreviousAddressRow);
    const monthsKey = formData.address_type === "OWN" ? "month_reside" : "paid_rent";
    const prevMonths = String(formData[monthsKey] ?? "");
    const nextMonths = String(next[monthsKey] ?? "");
    if (prevMonths !== nextMonths) {
      setFormData(next as AboutYourselfAddressEditFormData);
    }
    // Sync parent for Duration / previous-address months (Angular moveInValidate).
    onAddressChange(next as AboutYourselfAddressEditFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressType, formData.moveIn_date, formData.moveOut_date, formData.currently_living, formData.address_type]);

  // previousAddressMonths — months from this move-in to newer address move-in.
  useEffect(() => {
    if (addressType !== "previous_address") return;
    const moveInYmd = toComparableYmd(formData.moveIn_date);
    if (!moveInYmd) return;

    const endMoveInYmd = toComparableYmd(previousAddressEndMoveInDate);
    if (!endMoveInYmd) return;

    const row: Record<string, unknown> = {
      ...formData,
      address_type: formData.address_type,
      address_data: { moveIn_date: moveInYmd },
    };
    const months = computePreviousRowMonths(
      row,
      addressIndex ?? 0,
      endMoveInYmd,
      [],
    );
    if (months === null) return;

    const field = formData.address_type === "OWN" ? "month_reside" : "paid_rent";
    // `computePreviousRowMonths` already clamps to >= 0, so a sub-1-month duration is "0".
    // Show "0" rather than an empty field (the read-only months input is required).
    const value = String(months);
    if (String(formData[field as keyof AboutYourselfAddressEditFormData] ?? "") === value) return;

    const ad = { moveIn_date: moveInYmd, [field]: value };
    const next = {
      ...formData,
      [field]: value,
      address_data: {
        ...(typeof addressData?.address_data === "object" && addressData.address_data
          ? addressData.address_data
          : {}),
        ...ad,
      },
    };
    setFormData(next as AboutYourselfAddressEditFormData);
    onAddressChange(next as AboutYourselfAddressEditFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addressType,
    addressIndex,
    previousAddressEndMoveInDate,
    formData.moveIn_date,
    formData.address_type,
  ]);

  const handleChange = (field: string, value: string | boolean) => {
    setAddressFieldsDirty((d) => ({ ...d, [field]: true }));
    const newFormData = { ...formData, [field]: value };
    // Clear move-out when move-in is after planned move-out (Angular mat min parity).
    if (field === "moveIn_date") {
      const yIn = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ""
      const yOut =
        typeof newFormData.moveOut_date === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(newFormData.moveOut_date)
          ? newFormData.moveOut_date
          : ""
      if (yIn && yOut && yOut < yIn) {
        newFormData.moveOut_date = "";
        setAddressFieldsDirty((d) => ({ ...d, moveOut_date: true }));
      }
    }
    // Angular parity: checking "Still here" clears move-out date.
    if (field === "currently_living" && value === true) {
      newFormData.moveOut_date = "";
    }
    if (field === "country") {
      newFormData.outside_us = !isUsCountry(String(value));
    }
    setFormData(newFormData);
    onAddressChange(newFormData);
  };

  const isOwn = formData.address_type === "OWN";
  const isCurrent = addressType === "current_address";
  const isPrevious = addressType === "previous_address";
  const requireRentAmount = (isCurrent && isOwn) || !isOwn;
  /** Applicant FamilyForm parity: FAMILY uses Contact labels on current and previous. */
  const contactOrLandlordLabel =
    formData.address_type === "FAMILY" ? "Contact" : "Landlord";
  const addrSection = isCurrent ? "current" : "previous";
  const showAddrField = (key: string) =>
    exposeInlineValidation || addressFieldsDirty[key];
  const addrFieldErr = (key: string, resolver: () => string | null) =>
    showAddrField(key) ? resolver() : null;

  const countryErr = addrFieldErr("country", () =>
    angErr.angularCountryError(formData.country),
  );
  const houseNoErr = addrFieldErr("house_no", () =>
    angErr.angularStreetNoError(formData.house_no),
  );
  const streetErr = addrFieldErr("street", () =>
    angErr.angularStreetNameError(formData.street),
  );
  const cityErr = addrFieldErr("city", () => angErr.angularCityError(formData.city));
  const stateErr = addrFieldErr("state", () => angErr.angularStateError(formData.state));
  const zipErr = addrFieldErr("zip_code", () => angErr.angularZipError(formData.zip_code));
  const addressTypeErr = addrFieldErr("address_type", () =>
    angErr.angularAddressTypeError(formData.address_type),
  );
  const landlordNameErr = addrFieldErr("landlord_name", () =>
    angErr.angularLandlordNameError(
      formData.landlord_name ?? "",
      formData.address_type ?? "RENT",
      addrSection,
    ),
  );
  const contactRelationshipErr = addrFieldErr("landlord_relationship_type", () =>
    angErr.angularContactRelationshipError(formData.landlord_relationship_type ?? ""),
  );
  const landlordEmailErr = addrFieldErr("landlord_email", () =>
    angErr.angularLandlordEmailError(
      formData.landlord_email ?? "",
      formData.address_type ?? "RENT",
      addrSection,
    ),
  );
  const landlordMobileErr = addrFieldErr("landlord_mobile_no", () =>
    angErr.angularLandlordMobileError(
      formData.landlord_mobile_no ?? "",
      formData.address_type ?? "RENT",
      addrSection,
    ),
  );
  const moveInErr = addrFieldErr("moveIn_date", () =>
    angErr.angularMoveInError(formData.moveIn_date),
  );
  const moveOutErr = addrFieldErr("moveOut_date", () =>
    angErr.angularCurrentMoveOutDateError(formData.moveOut_date ?? "", {
      addressType: formData.address_type ?? "RENT",
      stillHere: formData.currently_living ?? false,
      moveInDate: formData.moveIn_date ?? "",
    }),
  );
  const rentAmountErr =
    requireRentAmount && showAddrField("rent_amount")
      ? angErr.angularRentAmountError(formData.rent_amount)
      : null;
  const mortgageErr =
    showAddrField("rent_amount")
      ? angErr.angularMortgagePaymentError(formData.rent_amount)
      : null;

  const tid = (slug: string, suffix: string) =>
    aboutAddressTestId(addressType, addressIndex, slug, suffix);

  return (
    <div className={editStyles.addressForm}>
      {isCurrent && geocodeWarning ? (
        <div className={styles.warningAlert} role="alert" data-testid="current-address-geocode-warning">
          <p>{geocodeWarning}</p>
        </div>
      ) : null}

      {/* Address search (Angular has Google Places prefill input — current address restricted to US) */}
      <div className={`${editStyles.formRow} ${styles.addressSearchCountryRow}`}>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("address-search", "label")}>
            Address
          </label>
          <AddressAutocomplete
            value={formData.street_address ?? ""}
            onChange={(value) => handleChange("street_address", value)}
            countryRestriction={isCurrent ? "us" : undefined}
            onPlaceSelect={(place: PlaceResult) => {
              const parsed = parseAddressComponents(place);
              const formattedAddress = parsed.formattedAddress || "";
              const aptUnit = extractAptUnit(formattedAddress);
              const country = parsed.country || formData.country || "US";

              const next = {
                ...formData,
                street_address: formattedAddress,
                house_no: parsed.streetNumber || "",
                street: parsed.streetName || "",
                apartment: aptUnit || formData.apartment || "",
                city: parsed.city || "",
                state: parsed.state || "",
                zip_code: parsed.zipCode || "",
                country,
                outside_us: !isUsCountry(country),
              };

              setFormData(next as AboutYourselfAddressEditFormData);
              onAddressChange(next as AboutYourselfAddressEditFormData);
              setAddressFieldsDirty((d) => ({
                ...d,
                street_address: true,
                house_no: true,
                street: true,
                apartment: true,
                city: true,
                state: true,
                zip_code: true,
                country: true,
              }));
              setShowDetail(true);
              setGeocodeWarning(null);
            }}
            placeholder="Enter your address"
            label=""
          />
        </div>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("country", "label")}>
            Country<span className={editStyles.required}>*</span>
          </label>
          <input
            type="text"
            className={withInputError(editStyles.input, Boolean(countryErr))}
            value={formData.country}
            data-testid={tid("country", "input")}
            onChange={(e) => handleChange("country", e.target.value)}
            onBlur={() => void handleGeocodeCheck()}
            placeholder="Country"
            disabled={isCurrent}
          />
          <AngularShowControlError message={countryErr} />
        </div>
      </div>

      {!showDetail && addressType === "previous_address" ? null : (
        <>
          <div className={editStyles.formRow}>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("street-no", "label")}>
            Street No.<span className={editStyles.required}>*</span>
          </label>
          <input
            type="text"
            className={withInputError(editStyles.input, Boolean(houseNoErr))}
            value={formData.house_no}
            data-testid={tid("street-no", "input")}
                onChange={(e) => handleChange("house_no", e.target.value)}
            placeholder="Street No."
          />
          <AngularShowControlError message={houseNoErr} />
        </div>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("street-name", "label")}>
            Street Name<span className={editStyles.required}>*</span> (ex. Main)
          </label>
          <input
            type="text"
            className={withInputError(editStyles.input, Boolean(streetErr))}
            value={formData.street}
            data-testid={tid("street-name", "input")}
                onChange={(e) => handleChange("street", e.target.value)}
            placeholder="Street"
          />
          <AngularShowControlError message={streetErr} />
        </div>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("apt-unit", "label")}>Apt/Unit</label>
          <input
            type="text"
            className={editStyles.input}
            value={formData.apartment}
            data-testid={tid("apt-unit", "input")}
                onChange={(e) => handleChange("apartment", e.target.value)}
            placeholder="Apt/Unit"
          />
        </div>
      </div>

      <div className={editStyles.formRow}>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("city", "label")}>
            City<span className={editStyles.required}>*</span>
          </label>
          <input
            type="text"
            className={withInputError(editStyles.input, Boolean(cityErr))}
            value={formData.city}
            data-testid={tid("city", "input")}
                onChange={(e) => handleChange("city", e.target.value)}
            onBlur={() => void handleGeocodeCheck()}
            placeholder="City"
          />
          <AngularShowControlError message={cityErr} />
        </div>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("state", "label")}>
            State<span className={editStyles.required}>*</span>
          </label>
          {!isUsCountry(formData.country) ? (
            <input
              type="text"
              className={withInputError(editStyles.input, Boolean(stateErr))}
              value={formData.state}
              data-testid={tid("state", "input")}
                onChange={(e) => handleChange("state", e.target.value)}
              onBlur={() => void handleGeocodeCheck()}
              placeholder="State/Province"
            />
          ) : (
            <SearchableSelect
              triggerDataTestId={tid("state", "select")}
              options={US_STATE_OPTIONS.map((s) => ({
                value: s.code,
                label: s.name,
              }))}
              value={formData.state || ""}
              onChange={(value) => handleChange("state", String(value))}
              onBlur={() => void handleGeocodeCheck()}
              placeholder="Select State"
              searchPlaceholder="Search State"
              searchable={false}
              className={`${styles.aboutStateCustomSelect}${stateErr ? ` ${editStyles.inputError}` : ""}`}
            />
          )}
          <AngularShowControlError message={stateErr} />
        </div>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("zipcode", "label")}>
            Zipcode
            <span className={editStyles.required}>*</span>
          </label>
          <input
            type="text"
            className={withInputError(editStyles.input, Boolean(zipErr))}
            value={formData.zip_code}
            data-testid={tid("zipcode", "input")}
                onChange={(e) => handleChange("zip_code", e.target.value)}
            onBlur={() => void handleGeocodeCheck()}
            placeholder="Zipcode"
          />
          <AngularShowControlError message={zipErr} />
        </div>
      </div>

      {/* Type of Residence - Radio Buttons (Angular ordering: after address fields) */}
      <div className={editStyles.formRow}>
        <div className={editStyles.formGroup}>
          <label className={editStyles.label} data-testid={tid("address-type", "label")}>
            Type of residence<span className={editStyles.required}>*</span>
          </label>
          <div className={`${editStyles.radioGroup}${addressTypeErr ? ` ${editStyles.inputError}` : ""}`}>
            {Object.entries(ADDRESS_TYPES).map(([key, value]) => (
              <label key={key} className={editStyles.radioLabel} data-testid={tid(`address-type-${key.toLowerCase()}`, "label")}>
                <input
                  type="radio"
                  name={`address_type_${addressType}_${addressIndex ?? 0}`}
                  value={key}
                  checked={formData.address_type === key}
                  data-testid={tid(`address-type-${key.toLowerCase()}`, "input")}
                onChange={(e) => handleChange("address_type", e.target.value)}
                />
                <span>{value}</span>
              </label>
            ))}
          </div>
          <AngularShowControlError message={addressTypeErr} />
        </div>
      </div>

      {formData.address_type !== "OWN" && (
        <div className={editStyles.formRow}>
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("landlord-name", "label")}>
              {contactOrLandlordLabel} Name
              <span className={editStyles.required}>*</span>
            </label>
            <input
              type="text"
              className={withInputError(editStyles.input, Boolean(landlordNameErr))}
              value={formData.landlord_name}
              data-testid={tid("landlord-name", "input")}
                onChange={(e) => handleChange("landlord_name", e.target.value)}
              placeholder={`${contactOrLandlordLabel} Name`}
            />
            <AngularShowControlError message={landlordNameErr} />
          </div>
          {formData.address_type === "FAMILY" && (
              <div className={editStyles.formGroup}>
                <label className={editStyles.label} data-testid={tid("contact-relationship", "label")}>
                  Contact Relationship
                  <span className={editStyles.required}>*</span>
                </label>
                <select
                  className={withInputError(editStyles.select, Boolean(contactRelationshipErr))}
                  value={formData.landlord_relationship_type}
                  data-testid={tid("contact-relationship", "select")}
                onChange={(e) =>
                    handleChange("landlord_relationship_type", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {Object.entries(RELATIONSHIP_TYPES).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
                <AngularShowControlError message={contactRelationshipErr} />
              </div>
            )}
          <div className={editStyles.formGroup}>
            <label
              className={editStyles.label}
              title="Please provide a valid email address, preferably a company email address for apartment complexes or businesses. This may be necessary to verify your previous address or rental history."
             data-testid={tid("landlord-email", "label")}>
              {contactOrLandlordLabel}{" "}
              Email
              {(formData.address_type === "RENT" ||
                formData.address_type === "FAMILY" ||
                addressType === "previous_address") && (
                  <span className={editStyles.required}>*</span>
                )}
            </label>
            <div className={editStyles.inputWithButton}>
              <input
                type="email"
                className={withInputError(editStyles.input, Boolean(landlordEmailErr))}
                value={formData.landlord_email}
                data-testid={tid("landlord-email", "input")}
                onChange={(e) => handleChange("landlord_email", e.target.value)}
                placeholder={`${contactOrLandlordLabel} Email`}
                title="Please provide a valid email address, preferably a company email address for apartment complexes or businesses. This may be necessary to verify your previous address or rental history."
              />
            </div>
            <AngularShowControlError message={landlordEmailErr} />
          </div>
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("landlord-mobile", "label")}>
              {contactOrLandlordLabel}{" "}
              Mobile
              {(formData.address_type === "RENT" ||
                formData.address_type === "FAMILY" ||
                addressType === "previous_address") && (
                  <span className={editStyles.required}>*</span>
                )}
            </label>
            <input
              type="tel"
              className={withInputError(editStyles.input, Boolean(landlordMobileErr))}
              value={formatPhoneInput(formData.landlord_mobile_no ?? "")}
              data-testid={tid("landlord-mobile", "input")}
                onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                handleChange("landlord_mobile_no", digits);
              }}
              placeholder="(xxx) xxx-xxxx"
            />
            <AngularShowControlError message={landlordMobileErr} />
          </div>
        </div>
      )}
      <div className={editStyles.formRow}>
        <div className={editStyles.formGroup}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "0.3rem",
            }}
          >
            <label className={editStyles.label} style={{ marginBottom: 0 }} data-testid={tid("move-in-date", "label")}>
              Move In Date<span className={editStyles.required}>*</span>
            </label>
            {addressType === "current_address" && (
              <label className={editStyles.checkboxLabel} data-testid={tid("still-here", "checkbox-label")}>
                <input
                  type="checkbox"
                  checked={formData.currently_living}
                  data-testid={tid("still-here", "checkbox")}
                  onChange={(e) =>
                    handleChange("currently_living", e.target.checked)
                  }
                />
                <span>Still here</span>
              </label>
            )}
          </div>
          <div data-testid={tid("move-in-date", "input")}><EditDatePicker
            value={formData.moveIn_date || ""}
            onChange={(value) => handleChange("moveIn_date", value)}
            allowPastDates={true}
            allowFutureDates={true}
            displayFormat="mdy"
            placeholder="MM/DD/YYYY"
            className={inputErrorClass(Boolean(moveInErr))}
            maxDate={
              formData.moveOut_date
                ? (() => {
                  if (/^\d{4}-\d{2}-\d{2}$/.test(formData.moveOut_date)) {
                    const [year, month, day] = formData.moveOut_date.split("-").map(Number);
                    return new Date(year, month - 1, day);
                  }
                  const parsed = new Date(formData.moveOut_date);
                  return isNaN(parsed.getTime()) ? undefined : parsed;
                })()
                : undefined
            }
          />
          <AngularShowControlError message={moveInErr} />
          </div>
        </div>
        {addressType === "current_address" && (
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("move-out-date", "label")}>
              {formData.address_type === "RENT"
                ? "Lease End Date"
                : "Planned Move Out Date"}
              {!formData.currently_living && (
                <span className={editStyles.required}>*</span>
              )}
            </label>
            <EditDatePicker
              value={formData.moveOut_date || ""}
              onChange={(value) => handleChange("moveOut_date", value)}
              allowPastDates={true}
              allowFutureDates={true}
              disabled={formData.currently_living}
              displayFormat="mdy"
              placeholder="MM/DD/YYYY"
              className={inputErrorClass(Boolean(moveOutErr))}
              minDate={
                !formData.currently_living &&
                formData.moveIn_date &&
                /^\d{4}-\d{2}-\d{2}$/.test(formData.moveIn_date)
                  ? (() => {
                      const [y, m, d] = formData.moveIn_date.split("-").map(Number);
                      return new Date(y, m - 1, d);
                    })()
                  : undefined
              }
            />
            <AngularShowControlError message={moveOutErr} />
          </div>
        )}

        {/* Rent Amount and other grouped fields horizontally */}
        {formData.address_type !== "OWN" && (
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("rent-amount", "label")}>
              Rent Amount{requireRentAmount ? <span className={editStyles.required}>*</span> : null}
            </label>
            <CurrencyInput
              data-testid={tid("rent-amount", "input")}
              className={withInputError(editStyles.input, Boolean(rentAmountErr))}
              value={formData.rent_amount != null ? String(formData.rent_amount) : ""}
              onChange={(value) => handleChange("rent_amount", value)}
              placeholder="Rent Amount"
              maxDigitsBeforeDecimal={RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL}
              error={rentAmountErr ?? undefined}
            />
          </div>
        )}
        
        {formData.address_type !== "OWN" && addressType === "previous_address" && (
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("paid-rent-months", "label")}>
              How many months did you pay rent?
            </label>
            <input
              type="number"
              className={editStyles.input}
              value={formData.paid_rent}
              data-testid={tid("paid-rent-months", "input")}
              readOnly={isPrevious}
              onChange={(e) => handleChange("paid_rent", e.target.value)}
              placeholder="Paid Rent"
            />
          </div>
        )}

        {formData.address_type === "OWN" && addressType === "current_address" && (
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("mortgage-payment", "label")}>
              Monthly Mortgage Payment Amount
              <span className={editStyles.required}>*</span>
            </label>
            <CurrencyInput
              data-testid={tid("mortgage-payment", "input")}
              className={withInputError(editStyles.input, Boolean(mortgageErr))}
              value={formData.rent_amount != null ? String(formData.rent_amount) : ""}
              onChange={(value) => handleChange("rent_amount", value)}
              placeholder="$0.00"
              maxDigitsBeforeDecimal={RENT_AMOUNT_MAX_DIGITS_BEFORE_DECIMAL}
              error={mortgageErr ?? undefined}
            />
          </div>
        )}

        {isOwn && isPrevious && (
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("month-reside", "label")}>
              Number of months you resided at this address?
            </label>
            <input
              type="number"
              className={editStyles.input}
              value={formData.month_reside}
              data-testid={tid("month-reside", "input")}
              readOnly={isPrevious}
              onChange={(e) => handleChange("month_reside", e.target.value)}
              placeholder="Resided Months"
            />
          </div>
        )}
      </div>

      {/* Duration label (Angular shows this when it can compute it) */}
      {isCurrent && (isOwn ? formData.month_reside : formData.paid_rent) && (
        <div className={editStyles.formRow}>
          <div className={editStyles.formGroup}>
            <label className={editStyles.label} data-testid={tid("duration", "label")}>
              Duration : {isOwn ? formData.month_reside : formData.paid_rent} Months
            </label>
          </div>
        </div>
      )}

        </>
      )}
    </div>
  );
}

